import type { LucideIcon } from "lucide-react";
import { FileSignature, PackageCheck, Store } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { Indicadores } from "./api";
import { formatNumero } from "./cidades";

export type Variante = "padrao" | "tv";

interface KpiCardsProps {
  data?: Indicadores;
  isLoading: boolean;
  variante?: Variante;
}

export function KpiCards({ data, isLoading, variante = "padrao" }: KpiCardsProps) {
  const tv = variante === "tv";
  return (
    <div className={cn("grid gap-4", tv ? "h-full grid-cols-1 gap-8 lg:grid-cols-3" : "sm:grid-cols-3")}>
      <Kpi
        icone={FileSignature}
        titulo="Receitas assinadas digitalmente"
        valor={data?.receitasAssinadas}
        detalhe={data?.receitasAssinadasMesAtual != null ? `+${formatNumero(data.receitasAssinadasMesAtual)} neste mês` : undefined}
        isLoading={isLoading}
        tv={tv}
      />
      <Kpi
        icone={PackageCheck}
        titulo="Receitas dispensadas"
        valor={data?.receitasDispensadas}
        detalhe={
          data &&
          `${formatNumero(data.dispensacoesSemLogin)} sem login · ${formatNumero(data.dispensacoesSemCadastro)} de farmácia sem cadastro`
        }
        isLoading={isLoading}
        tv={tv}
      />
      <Kpi
        icone={Store}
        titulo="Farmácias cadastradas"
        valor={data?.farmaciasCadastradas}
        detalhe={
          data && `${formatNumero(data.farmaciasValidadas)} validadas · ${formatNumero(data.farmaciasPendentes)} pendentes`
        }
        isLoading={isLoading}
        tv={tv}
      />
    </div>
  );
}

interface KpiProps {
  icone: LucideIcon;
  titulo: string;
  valor?: number;
  detalhe?: string;
  isLoading: boolean;
  tv: boolean;
}

export function Kpi({ icone: Icone, titulo, valor, detalhe, isLoading, tv }: KpiProps) {
  return (
    <div
      className={cn(
        "flex flex-col rounded-xl border",
        tv
          ? // Telas baixas (< 900px): mais compacto, senão as 2 linhas do SNCR não cabem.
            "justify-center gap-3 border-white/10 bg-white/5 p-6 [@media(min-height:900px)]:gap-6 [@media(min-height:900px)]:p-10"
          : "gap-2 border-border bg-card p-5 shadow-card",
      )}
    >
      <div className={cn("flex items-center gap-3", tv ? "text-slate-300" : "text-muted-foreground")}>
        <Icone
          className={tv ? "h-8 w-8 text-teal-400 [@media(min-height:900px)]:h-10 [@media(min-height:900px)]:w-10" : "h-5 w-5 text-primary"}
          aria-hidden
        />
        <span className={tv ? "text-2xl font-medium [@media(min-height:900px)]:text-3xl" : "text-sm font-medium"}>{titulo}</span>
      </div>
      {isLoading || valor === undefined ? (
        <Skeleton className={tv ? "h-16 w-2/3 bg-white/10 [@media(min-height:900px)]:h-28" : "h-9 w-24"} />
      ) : (
        <span
          className={cn("font-semibold tabular-nums", tv ? "text-6xl text-white [@media(min-height:900px)]:text-8xl" : "text-3xl text-foreground")}
          data-testid={`kpi-${titulo}`}
        >
          {formatNumero(valor)}
        </span>
      )}
      {detalhe && <span className={tv ? "text-xl text-slate-400 [@media(min-height:900px)]:text-2xl" : "text-xs text-muted-foreground"}>{detalhe}</span>}
    </div>
  );
}
